import React, { useState, useEffect } from 'react';
import { doc, deleteDoc, writeBatch, collection, getDocs, query, where } from 'firebase/firestore';

// Helper for API call defined outside to prevent re-creation
const fetchTruTacData = async (start, end) => {
    console.log(`[TruTac] Fetching from ${start.toISOString()} to ${end.toISOString()}`);
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout

        const requestBody = { 
            recordsAfterDateTime: start.toISOString(), 
            recordsBeforeDateTime: end.toISOString(), 
            includeEmptyDates: true, 
            includeDriversWithNoEntries: true 
        };
        
        // Using Firebase Cloud Function backend proxy
        const response = await fetch("https://us-central1-weekend-availability-f7147.cloudfunctions.net/trutacProxy", {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json' 
            },
            body: JSON.stringify(requestBody),
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText.substring(0, 100)}`);
        }
        
        const text = await response.text();
        if (!text) return { success: true, data: [] };
        
        try {
            const parsed = JSON.parse(text);
            console.log('[TruTac] Raw API Response:', parsed);
            // Handle if API returns { data: [...] } or directly returns [...]
            const data = Array.isArray(parsed) ? parsed : (parsed.data || []);
            console.log(`[TruTac] Received ${data.length} records`);
            return { success: true, data: data };
        } catch (parseError) {
            console.error('[TruTac] Parse Error:', parseError, 'Text:', text.substring(0, 200));
            throw new Error(`Invalid JSON response`);
        }
        
    } catch (e) {
        console.error("TruTac Fetch Error:", e);
        let msg = e && e.message ? e.message : String(e);
        if (e && e.name === 'AbortError') msg = 'Timeout waiting for API';
        if (msg.includes('Failed to fetch')) msg = 'Network error or CORS issue: Failed to fetch';
        return { success: false, error: msg };
    }
};

const RestCalculator = ({ db, usersList, currentDate, managerData, fetchManagerData, setSaveMessage }) => {
    const [calculatorDay, setCalculatorDay] = useState('');
    const [calcInputs, setCalcInputs] = useState({});
    const [compensationUsage, setCompensationUsage] = useState({}); 
    const [futureRestOptions, setFutureRestOptions] = useState({}); 
    const [isAutoCalculating, setIsAutoCalculating] = useState(false);
    const [localStatus, setLocalStatus] = useState(''); 
    const [expandedReportId, setExpandedReportId] = useState(null);
    const [manualAddDriverId, setManualAddDriverId] = useState('');
    const [restWarnings, setRestWarnings] = useState({}); // Track drivers with <24hr rest
    // manual entry form fields
    const [manualRestDate, setManualRestDate] = useState('');
    const [manualRestHours, setManualRestHours] = useState('');
    const [manualRestNotes, setManualRestNotes] = useState('');

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const roundUpToQuarter = (d) => { const m = d.getMinutes(); const r = m % 15; if (r === 0) return d; d.setMinutes(m + (15 - r)); d.setSeconds(0); return d; };
    const roundDownToQuarter = (d) => { const m = d.getMinutes(); const r = m % 15; d.setMinutes(m - r); d.setSeconds(0); return d; };
    const selectedSaturdayDate = calculatorDay ? new Date(currentDate.getFullYear(), currentDate.getMonth(), Number(calculatorDay)) : null;

    const getCalculatedValues = (userData) => {
        const { startDay, startTime, endDay, endTime, restTaken } = userData;
        if (!startDay || !startTime || !endDay || !endTime) return null;
        const diffHrs = (new Date(`${endDay}T${endTime}`) - new Date(`${startDay}T${startTime}`)) / (1000 * 60 * 60);
        if (diffHrs < 0) return { error: "Invalid times" };
        if (diffHrs >= 45) return { hoursTaken: diffHrs.toFixed(2), isFullRest: true, deficit: "0.00", totalDue: "0.00" };
        const deficit = (45 - diffHrs);
        if (restTaken) return { hoursTaken: diffHrs.toFixed(2), isFullRest: false, deficit: "0.00", totalDue: "0.00", cleared: true };
        return { hoursTaken: diffHrs.toFixed(2), isFullRest: false, deficit: deficit.toFixed(2), totalDue: (deficit + 45).toFixed(2), cleared: false };
    };

    const calculateRestData = (initials) => { return getCalculatedValues(calcInputs[initials] || {}); };

    const formatHours = (hrs) => {
        if (hrs === undefined || hrs === null || Number.isNaN(Number(hrs))) return '-';
        return Number(hrs).toFixed(2);
    };

    const updateRestWarningForInputs = (initials, inputs) => {
        if (!inputs) return;
        const { startDay, startTime, endDay, endTime } = inputs;
        if (!startDay || !startTime || !endDay || !endTime) return;

        const diffHrs = (new Date(`${endDay}T${endTime}`) - new Date(`${startDay}T${startTime}`)) / (1000 * 60 * 60);
        if (diffHrs >= 24) {
            setRestWarnings(prev => {
                if (!prev[initials]) return prev;
                const next = { ...prev };
                delete next[initials];
                return next;
            });
            return;
        }

        if (diffHrs > 0) {
            setRestWarnings(prev => ({
                ...prev,
                [initials]: `⚠️ Longest rest only ${diffHrs.toFixed(1)}h (not 24h minimum)`
            }));
        }
    };

    // --- DATA FETCHING & ANALYSIS ---

    const handleAutoCalcRestFromTruTac = async () => {
        if (!calculatorDay) {
            setLocalStatus("⚠️ Please select a date first!");
            return;
        }
        
        setIsAutoCalculating(true);
        setLocalStatus("⏳ Connecting..."); // Immediate Feedback

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const satDay = Number(calculatorDay);
        
        const satDate = new Date(year, month, satDay);
        const weekendSaturday = new Date(satDate);
        weekendSaturday.setHours(0,0,0,0);

        const mondayBeforeWeekend = new Date(weekendSaturday);
        mondayBeforeWeekend.setDate(weekendSaturday.getDate() - 5);
        mondayBeforeWeekend.setHours(0,0,0,0);

        const sundayOfWeekend = new Date(weekendSaturday);
        sundayOfWeekend.setDate(weekendSaturday.getDate() + 1);
        sundayOfWeekend.setHours(0,0,0,0);

        const fetchStart = new Date(mondayBeforeWeekend);
        fetchStart.setDate(fetchStart.getDate() - 1); // include prior shifts that cross into Monday
        fetchStart.setHours(0,0,0,0);

        const fetchEnd = new Date(sundayOfWeekend);
        fetchEnd.setDate(fetchEnd.getDate() + 7); // allow rest to end after the weekend
        fetchEnd.setHours(23,59,59,999);

        try {
            const result = await fetchTruTacData(fetchStart, fetchEnd);
            
            if (!result || !result.success) {
                 const errMsg = result?.error || "Unknown Error";
                 setLocalStatus(`❌ API Failed: ${errMsg}`);
                 console.error(errMsg);
                 setIsAutoCalculating(false);
                 return;
            }
            
            const data = result.data;
            
            if (!Array.isArray(data) || data.length === 0) {
                setLocalStatus(`⚠️ API returned 0 records.`);
                setIsAutoCalculating(false);
                return;
            }

            const newInputs = {};
            const newWarnings = {};
            let matchedCount = 0;

            usersList.forEach(user => {
                // Improved Matching Logic: Check if parts of the name exist in TruTac name
                const localNameParts = user.displayName.toLowerCase().split(' ').filter(p => p.length > 2);
                
                const apiDriver = data.find(d => {
                    const driverNameLower = d.driverName.toLowerCase();
                    // Match if ALL significant parts of local name are found in TruTac name
                    return localNameParts.every(part => driverNameLower.includes(part));
                });
                
                if (apiDriver && apiDriver.records) {
                    const shifts = [];
                    apiDriver.records.forEach(r => {
                        let s = r.manualEntryStart?.date || r.firstCardIn?.date;
                        let e = r.manualEntryEnd?.date || r.lastCardOut?.date;
                        if (s && e) shifts.push({ start: new Date(s), end: new Date(e) });
                    });
                    shifts.sort((a, b) => a.start - b.start);
                    
                    // Filter: Worked Weekend
                    const weekendStart = new Date(year, month, satDay); weekendStart.setHours(0,0,0,0);
                    const weekendEnd = new Date(weekendStart); weekendEnd.setDate(weekendEnd.getDate() + 2);

                    const workedWeekend = shifts.some(shift => shift.start < weekendEnd && shift.end > weekendStart);
                    
                    if (workedWeekend) {
                        let longestGap = { duration: 0, start: null, end: null };

                        for (let i = 0; i < shifts.length - 1; i++) {
                            const currentEnd = shifts[i].end;
                            const nextStart = shifts[i+1].start;
                            const duration = (nextStart - currentEnd) / (1000 * 60 * 60); // hours

                            // Only consider gaps that start after the Monday before the weekend and before Sunday 00:00
                            if (currentEnd >= mondayBeforeWeekend && currentEnd < sundayOfWeekend && nextStart <= fetchEnd && duration > longestGap.duration) {
                                longestGap = { duration, start: currentEnd, end: nextStart };
                            }
                        }

                        if (longestGap.start && longestGap.end) {
                            const s = roundUpToQuarter(new Date(longestGap.start));
                            const e = roundDownToQuarter(new Date(longestGap.end));
                            newInputs[user.initials] = {
                                startDay: s.toISOString().split('T')[0],
                                startTime: s.toTimeString().slice(0, 5),
                                endDay: e.toISOString().split('T')[0],
                                endTime: e.toTimeString().slice(0, 5)
                            };
                            
                            // Flag if longest rest period is less than 24 hours
                            if (longestGap.duration < 24) {
                                newWarnings[user.initials] = `⚠️ Longest rest only ${longestGap.duration.toFixed(1)}h (not 24h minimum)`;
                            }
                            
                            matchedCount++;
                        }
                    }
                }
            });

            // Restore saved calculations completely (all fields including edited times)
            // This ensures if a calculation was already saved and edited, those values are preserved
            managerData.filter(r => r.day === satDay && r.month === month && r.year === year && r.restCalc).forEach(saved => {
                newInputs[saved.initials] = {
                    startDay: saved.restCalc.startDay,
                    startTime: saved.restCalc.startTime,
                    endDay: saved.restCalc.endDay,
                    endTime: saved.restCalc.endTime,
                    restTaken: saved.restCalc.restTaken || false,
                    compensatedBy: saved.restCalc.compensatedBy,
                    notes: saved.restCalc.notes || ''
                };
            });

            setCalcInputs(newInputs);
            setRestWarnings(newWarnings);
            
            if (matchedCount > 0) {
                setLocalStatus(`✅ Found ${matchedCount} weekend workers.`);
            } else {
                setLocalStatus("⚠️ No matching weekend work found.");
            }

        } catch (e) {
            console.error(e);
            setLocalStatus("❌ System Error");
        } finally {
            setIsAutoCalculating(false);
            // Clear success message after 5 seconds
            setTimeout(() => {
                 setLocalStatus(prev => prev.includes('✅') ? '' : prev); 
            }, 5000);
        }
    };

    const handleCheckFutureRest = async (user, weeklyRequired) => {
        if (!calculatorDay) return;
        const initials = user.initials;
        setLocalStatus(`🔎 Checking ${user.displayName}...`);
        
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const satDay = Number(calculatorDay);
        const satDate = new Date(year, month, satDay);
        
        // Look 21 days forward from the weekend
        const fetchStart = new Date(satDate); 
        fetchStart.setDate(satDate.getDate() + 1); 
        fetchStart.setHours(0,0,0,0);
        const fetchEnd = new Date(satDate); 
        fetchEnd.setDate(satDate.getDate() + 22);
        fetchEnd.setHours(23,59,59,999); 

        try {
            const result = await fetchTruTacData(fetchStart, fetchEnd);
            
            if (!result || !result.success) {
                setLocalStatus(`❌ API Failed: ${result?.error}`);
                return;
            }
            
            const data = result.data;
            if (!Array.isArray(data) || data.length === 0) {
                setLocalStatus(`⚠️ No shift data found in next 21 days.`);
                return;
            }
            // Use improved matching logic like auto-calc  
            const localNameParts = user.displayName.toLowerCase().split(' ').filter(p => p.length > 2);
            const apiDriver = data.find(d => {
                const driverNameLower = d.driverName.toLowerCase();
                return localNameParts.every(part => driverNameLower.includes(part));
            });

            if (!apiDriver || !apiDriver.records) {
                setLocalStatus(`⚠️ No data found for ${user.displayName}.`);
                return;
            }
            
                const shifts = [];
                apiDriver.records.forEach(r => {
                    let s = r.manualEntryStart?.date || r.firstCardIn?.date;
                    let e = r.manualEntryEnd?.date || r.lastCardOut?.date;
                    if (s && e) shifts.push({ start: new Date(s), end: new Date(e) });
                });
                shifts.sort((a, b) => a.start - b.start);

                let foundOption = null;

                // helper to round down to quarter hours
                const roundDownQuarter = hrs => Math.floor(hrs * 4) / 4;

                // Only accept rest periods that are between actual shifts (have definite start AND end)
                if (shifts.length >= 2) {
                    for (let i = 0; i < shifts.length - 1; i++) {
                        const currentShiftEnd = shifts[i].end;
                        const nextShiftStart = shifts[i+1].start;
                        const rawGap = (nextShiftStart - currentShiftEnd) / (1000 * 60 * 60);
                        const gapHours = roundDownQuarter(rawGap);

                        if (gapHours >= weeklyRequired) {
                            foundOption = {
                                startDay: currentShiftEnd.toISOString().split('T')[0],
                                hours: gapHours.toFixed(2),
                                startObj: currentShiftEnd
                            };
                            break;
                        }
                    }
                }

                // foundOption comes from real API data
                if (foundOption) {
                    // ensure driver has a row so the "Use" button is visible
                    setCalcInputs(prev => ({
                        ...prev,
                        [initials]: prev[initials] || { startDay: '', startTime: '', endDay: '', endTime: '', restTaken: false }
                    }));

                    setFutureRestOptions(prev => ({
                        ...prev,
                        [initials]: foundOption
                    }));
                    setLocalStatus(`✅ Found ${foundOption.hours}h rest on ${foundOption.startDay}!`);
                } else {
                    setLocalStatus(`⚠️ ${user.displayName}: No rest period ≥ ${weeklyRequired}h found between shifts in next 21 days. No valid compensation rest available.`);
                }
        } catch (e) {
            console.error(e);
            setLocalStatus("❌ Error checking future");
        }
    };

    const handleApplyCompensation = (initials, option) => {
        // derive end day/time from start and hours
        const start = new Date(option.startDay + 'T00:00');
        const hrs = parseFloat(option.hours);
        const end = new Date(start);
        end.setHours(end.getHours() + hrs);
        const endDay = end.toISOString().split('T')[0];
        const endTime = end.toTimeString().slice(0,5);

        setCalcInputs(prev => {
            const inputs = {
                ...prev[initials],
                startDay: option.startDay,
                startTime: '00:00',
                endDay,
                endTime,
                restTaken: true,
                compensatedBy: {
                    date: option.startDay, 
                    startDay: option.startDay, 
                    hours: option.hours
                }
            };
            updateRestWarningForInputs(initials, inputs);
            return {
                ...prev,
                [initials]: inputs
            };
        });
        setFutureRestOptions(prev => {
            const next = {...prev};
            delete next[initials];
            return next;
        });
    };

    const handleManualAllocate = (initials) => {
        // prompt user for a future rest period to apply manually (DD/MM/YYYY)
        const dateStr = window.prompt('Enter rest start date (DD/MM/YYYY):');
        if (!dateStr) return;
        const parts = dateStr.split('/');
        if (parts.length !== 3) return alert('Date must be DD/MM/YYYY');
        const [dd, mm, yyyy] = parts;
        const date = new Date(`${yyyy}-${mm}-${dd}`);
        if (isNaN(date)) return alert('Invalid date');
        const isoDate = date.toISOString().split('T')[0];

        const hoursStr = window.prompt('Enter hours rested:');
        if (!hoursStr) return;
        const hours = parseFloat(hoursStr);
        if (isNaN(hours) || hours <= 0) {
            alert('Invalid hours');
            return;
        }
        const end = new Date(date);
        end.setHours(end.getHours() + hours);
        const endDay = end.toISOString().split('T')[0];
        const endTime = end.toTimeString().slice(0,5);

        const inputs = {
            startDay: isoDate,
            startTime: '00:00',
            endDay,
            endTime,
            restTaken: true,
            notes: ''
        };

        setCalcInputs(prev => ({
            ...prev,
            [initials]: inputs
        }));
        updateRestWarningForInputs(initials, inputs);
    };

    useEffect(() => {
        setCalcInputs({}); 
        setFutureRestOptions({}); 
        setCompensationUsage({});
        setRestWarnings({});
        setLocalStatus('');
        setManualAddDriverId('');
        setManualRestDate('');
        setManualRestHours('');
        setManualRestNotes('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calculatorDay, currentDate]);

    const updateCalculatorInput = (initials, field, value) => {
        setCalcInputs(prev => {
            const updated = { ...prev, [initials]: { ...prev[initials], [field]: value } };
            updateRestWarningForInputs(initials, updated[initials]);
            return updated;
        });
    };

    const handleManualAddDriver = (driverId) => {
        if (!driverId) return;
        // open manual entry form (driverId stored separately)
        setManualAddDriverId(driverId);
        // reset the manual field values
        setManualRestDate('');
        setManualRestHours('');
        setManualRestNotes('');
    };

    const handleSubmitManualEntry = () => {
        if (!manualAddDriverId || !manualRestDate || !manualRestHours) {
            alert('Please supply date and hours for manual rest.');
            return;
        }
        const start = new Date(manualRestDate);
        const startDay = manualRestDate;
        const startTime = '00:00';
        const end = new Date(start);
        end.setHours(end.getHours() + parseFloat(manualRestHours));
        const endDay = end.toISOString().split('T')[0];
        const endTime = end.toTimeString().slice(0,5);

        const inputs = {
            startDay,
            startTime,
            endDay,
            endTime,
            restTaken: true,
            notes: manualRestNotes || ''
        };

        setCalcInputs(prev => ({
            ...prev,
            [manualAddDriverId]: inputs
        }));
        updateRestWarningForInputs(manualAddDriverId, inputs);
        setManualAddDriverId('');
        setManualRestDate('');
        setManualRestHours('');
        setManualRestNotes('');
    };

    const handleSaveRestCalculations = async () => {
        if (!calculatorDay) return;
        const batch = writeBatch(db);
        Object.keys(calcInputs).forEach(init => {
            const inputs = calcInputs[init];
            const results = getCalculatedValues(inputs);
            const user = usersList.find(u => u.initials === init);
            const docId = `${init}_${currentDate.getFullYear()}_${currentDate.getMonth()}_${calculatorDay}`;
            
            // Build restCalc object, filtering out undefined values
            const restCalc = { ...inputs, ...results, notes: inputs.notes || '' };
            // Remove any undefined fields (Firebase doesn't allow them)
            Object.keys(restCalc).forEach(key => {
                if (restCalc[key] === undefined) {
                    delete restCalc[key];
                }
            });
            
            const saveData = {
                initials: init,
                displayName: user ? user.displayName : init,
                year: currentDate.getFullYear(),
                month: currentDate.getMonth(),
                day: Number(calculatorDay),
                restCalc: restCalc
            };

            batch.set(doc(db, "availability", docId), saveData, { merge: true });
        });
        await batch.commit();
        setLocalStatus('✅ Saved!');
        if(setSaveMessage) setSaveMessage('✅ Saved!');
        fetchManagerData();
        setTimeout(() => setLocalStatus(''), 3000);
    };

    const handleDeleteRestCalculation = async (docId) => {
        if (window.confirm("Delete record?")) {
            await deleteDoc(doc(db, "availability", docId));
            fetchManagerData();
        }
    };

    const handleGenerateRestReport = () => {
        // build list from any drivers we've entered data for (manual or auto)
        const initialsSet = new Set(Object.keys(calcInputs));
        const drivers = Array.from(initialsSet).map(init => {
            const u = usersList.find(u => u.initials === init);
            return u || { initials: init, displayName: init };
        });

        const w = window.open('', '_blank');
        const reportTitle = selectedSaturdayDate
            ? `Rest Report - ${selectedSaturdayDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
            : `Rest Report - Sat ${calculatorDay}`;
        w.document.write(`<html><body><h2>${reportTitle}</h2><table border="1" cellpadding="5"><thead><tr><th>Driver</th><th>Taken</th><th>Deficit</th><th>Target (+45)</th><th>Total</th><th>Notes</th></tr></thead><tbody>`);
        drivers.forEach(d => {
            const c = calculateRestData(d.initials);
                let def = 0, target = 0, taken = 0;
            const inputs = calcInputs[d.initials] || {};
            if (c) {
                taken = c.hoursTaken;
                if (!c.isFullRest) { def = Number(c.deficit); target = 45 + def; }
            }
            const total = inputs.restTaken ? 45 : target;
            w.document.write(`<tr><td>${d.displayName}</td><td>${taken}</td><td>${def}</td><td>${target}</td><td>${total}</td><td>${inputs.notes || ''}</td></tr>`);
        });
        w.document.write(`</tbody></table></body></html>`);
        w.document.close();
    };

    const weekendSaturdays = [];
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), d);
        if (dateObj.getDay() === 6) { weekendSaturdays.push(d); }
    }

    const visibleDrivers = usersList.filter(u => calcInputs[u.initials]);
    
    // Rota Cross-Check List (Split by Day)
    const satDayNum = Number(calculatorDay);
    const selectedSaturday = calculatorDay
        ? new Date(currentDate.getFullYear(), currentDate.getMonth(), satDayNum)
        : null;
    const selectedSunday = selectedSaturday
        ? new Date(selectedSaturday.getFullYear(), selectedSaturday.getMonth(), selectedSaturday.getDate() + 1)
        : null;

    const rotaDriversSat = selectedSaturday
        ? managerData.filter(d => (
            d.day === selectedSaturday.getDate()
            && d.month === selectedSaturday.getMonth()
            && d.year === selectedSaturday.getFullYear()
            && d.managerDecision === 'Working'
        ))
        : [];

    const rotaDriversSun = selectedSunday
        ? managerData.filter(d => (
            d.day === selectedSunday.getDate()
            && d.month === selectedSunday.getMonth()
            && d.year === selectedSunday.getFullYear()
            && d.managerDecision === 'Working'
        ))
        : [];

    return (
        <div className="animate-fade-in">
            <div className="card" style={{ maxWidth: '400px', margin: '0 auto 20px auto' }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Select Weekend (Saturday):</label>
                <select value={calculatorDay} onChange={e => setCalculatorDay(e.target.value)}>
                    <option value="">-- Select Date --</option>
                    {weekendSaturdays.map(d => <option key={d} value={d}>Saturday {d} {monthNames[currentDate.getMonth()]}</option>)}
                </select>
            </div>

            {calculatorDay && (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <select
                        value={manualAddDriverId}
                        onChange={(e) => handleManualAddDriver(e.target.value)}
                        style={{ width: '300px', marginRight: '10px' }}
                    >
                        <option value="">+ Add Manual Driver to Table...</option>
                        {usersList.map(u => <option key={u.initials} value={u.initials}>{u.displayName}</option>)}
                    </select>
                </div>
            )}

            {calculatorDay && (
                <>
                    {manualAddDriverId && (
                        <div className="card" style={{ maxWidth: '400px', margin: '0 auto 20px auto' }}>
                            <h4 style={{ marginBottom: '10px' }}>
                                Manual Rest Entry for {usersList.find(u => u.initials === manualAddDriverId)?.displayName}
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label>
                                    Date Rest Started:
                                    <input type="date" value={manualRestDate} onChange={e => setManualRestDate(e.target.value)} />
                                </label>
                                <label>
                                    Hours Rested:
                                    <input type="number" step="0.25" value={manualRestHours} onChange={e => setManualRestHours(e.target.value)} />
                                </label>
                                <label>
                                    Notes (optional):
                                    <input type="text" value={manualRestNotes} onChange={e => setManualRestNotes(e.target.value)} />
                                </label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button onClick={handleSubmitManualEntry} style={{ background: 'var(--success)', color: 'white' }}>Add</button>
                                    <button onClick={() => setManualAddDriverId('')} style={{ background: 'var(--danger)', color: 'white' }}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div className="card" style={{ flex: 1, minWidth: '60%' }}>
                        <div style={{ overflowX: 'visible', overflowY: 'auto', maxHeight: '70vh', fontSize: '12px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--card-bg)', zIndex: 10 }}>
                                    <tr>
                                        <th style={{ padding: '6px 4px', textAlign: 'left', fontWeight: 'bold', fontSize: '11px' }}>Driver</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px', minWidth: '90px' }}>Rest Start</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px', minWidth: '90px' }}>Rest End</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>Hrs</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>Def</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>Weekly</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>Total</th>
                                        <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>✓</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleDrivers.length === 0 ? (
                                        <tr><td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No work records found. Click "Auto-Calc" or Add a Driver manually.</td></tr>
                                    ) : (
                                        visibleDrivers.map(u => {
                                            const calc = calculateRestData(u.initials);
                                            const inputs = calcInputs[u.initials] || {};
                                            const currentDeficit = calc ? Number(calc.deficit) : 0;
                                            const weeklyReq = 45 + currentDeficit;
                                            const totalDue = inputs.restTaken ? 45 : weeklyReq;
                                            
                                            const foundOption = futureRestOptions[u.initials];
                                            const isCompensatedByFuture = inputs.compensatedBy;
                                            const compensationWarning = compensationUsage[u.initials];
                                            const restWarning = restWarnings[u.initials];

                                            return (
                                                <tr key={u.initials} style={{background: compensationWarning ? 'rgba(217,119,6,0.12)' : 'transparent', fontSize: '12px'}}>
                                                    <td style={{ fontWeight: '500', padding: '4px', borderBottom: '1px solid var(--border)' }}>
                                                        <div>{u.displayName}</div>
                                                        {restWarning && <div style={{fontSize:'9px', color:'#f59e0b', fontWeight:'bold', marginTop: '2px'}}>{restWarning}</div>}
                                                        {compensationWarning && <div style={{fontSize:'9px', color:'var(--warning)', fontWeight:'bold', marginTop: '2px'}}>{compensationWarning}</div>}
                                                    </td>
                                                    {inputs.restTaken ? (
                                                        <>
                                                            <td style={{ padding: '4px', borderBottom: '1px solid var(--border)' }}>
                                                                <div>{inputs.startDay} {inputs.startTime}</div>
                                                                {isCompensatedByFuture && <div style={{fontSize:'9px', color:'var(--success)'}}>comp by {isCompensatedByFuture.hours}h</div>}
                                                            </td>
                                                            <td style={{ padding: '4px', borderBottom: '1px solid var(--border)' }}>
                                                                <div>{inputs.endDay} {inputs.endTime}</div>
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td style={{ padding: '4px', borderBottom: '1px solid var(--border)' }}>
                                                                <input type="date" value={inputs.startDay || ''} onChange={e => updateCalculatorInput(u.initials, 'startDay', e.target.value)} style={{ width: '85px', fontSize: '11px', padding: '2px' }} />
                                                                <input type="time" value={inputs.startTime || ''} onChange={e => updateCalculatorInput(u.initials, 'startTime', e.target.value)} style={{ width: '70px', fontSize: '10px', padding: '2px', marginTop: '2px', display: 'block' }} />
                                                            </td>
                                                            <td style={{ padding: '4px', borderBottom: '1px solid var(--border)' }}>
                                                                <input type="date" value={inputs.endDay || ''} onChange={e => updateCalculatorInput(u.initials, 'endDay', e.target.value)} style={{ width: '85px', fontSize: '11px', padding: '2px' }} />
                                                                <input type="time" value={inputs.endTime || ''} onChange={e => updateCalculatorInput(u.initials, 'endTime', e.target.value)} style={{ width: '70px', fontSize: '10px', padding: '2px', marginTop: '2px', display: 'block' }} />
                                                            </td>
                                                        </>
                                                    )}
                                                    <td style={{ fontWeight: 'bold', padding: '4px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>{calc ? calc.hoursTaken : '-'}</td>
                                                    <td style={{ color: 'var(--danger)', fontWeight: 'bold', padding: '4px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>{inputs.restTaken ? '0' : (calc ? calc.deficit : '-')}</td>
                                                    <td style={{ fontWeight: 'bold', padding: '4px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>{inputs.restTaken ? '0.00' : formatHours(weeklyReq)}</td>
                                                    <td style={{ fontWeight: 'bold', color: 'var(--danger)', padding: '4px', borderBottom: '1px solid var(--border)', textAlign: 'center', minWidth: '60px' }}>
                                                        <div>{formatHours(totalDue)}</div>
                                                        {totalDue > 45 && !inputs.restTaken && (
                                                            <div style={{marginTop:'4px', display:'flex', gap:'2px', justifyContent: 'center'}}>
                                                                <button 
                                                                    onClick={() => handleCheckFutureRest(u, weeklyReq)} 
                                                                    style={{fontSize:'9px', padding:'2px 4px', background:'var(--primary)', color:'white', border:'none', borderRadius:'3px', cursor:'pointer'}}
                                                                    title="Find rest period"
                                                                >
                                                                    Find
                                                                </button>
                                                                <button
                                                                    onClick={() => handleManualAllocate(u.initials)}
                                                                    style={{fontSize:'9px', padding:'2px 4px', background:'var(--dark)', color:'white', border:'none', borderRadius:'3px', cursor:'pointer'}}
                                                                    title="Manually allocate rest"
                                                                >
                                                                    Add
                                                                </button>
                                                            </div>
                                                        )}
                                                        {foundOption && !inputs.restTaken && (
                                                             <div style={{marginTop:'4px', fontSize:'9px', background:'rgba(94,106,210,0.12)', padding:'3px', borderRadius:'3px', border:'1px solid var(--primary)'}}>
                                                                 <div>{foundOption.hours}h {foundOption.startDay}</div>
                                                                 <button 
                                                                    onClick={() => handleApplyCompensation(u.initials, foundOption)}
                                                                    style={{width:'100%', marginTop:'2px', background:'var(--success)', color:'white', border:'none', cursor:'pointer', fontSize: '9px', padding: '2px'}}
                                                                 >
                                                                     Use
                                                                 </button>
                                                             </div>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '4px', borderBottom: '1px solid var(--border)' }}><input type="checkbox" checked={inputs.restTaken || false} onChange={e => updateCalculatorInput(u.initials, 'restTaken', e.target.checked)} style={{ width: '16px', height: '16px' }} /></td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                            {localStatus && <span style={{fontWeight:'bold', fontSize:'13px', color: localStatus.includes('✅') ? 'green' : (localStatus.includes('⚠️') ? '#d97706' : 'red')}}>{localStatus}</span>}
                            <button onClick={handleAutoCalcRestFromTruTac} disabled={isAutoCalculating} style={{ background: 'var(--dark)', color: 'white' }}>{isAutoCalculating ? 'Calculating...' : '🔮 Auto-Calc from TruTac'}</button>
                            <button onClick={handleGenerateRestReport} style={{ background: 'var(--warning)', color: 'black' }}>📄 Generate Report</button>
                            <button onClick={handleSaveRestCalculations} style={{ background: 'var(--success)' }}>Save Calculation</button>
                        </div>

                        <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                            <h4 style={{ fontSize: '12px', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Saved Calculations (Click to View)</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {managerData.filter(d => d.restCalc && selectedSaturdayDate && d.day === selectedSaturdayDate.getDate() && d.month === selectedSaturdayDate.getMonth() && d.year === selectedSaturdayDate.getFullYear()).length === 0 ? (
                                    <p className="text-sm">No saved calculations for this date.</p>
                                ) : (
                                    managerData.filter(d => d.restCalc && selectedSaturdayDate && d.day === selectedSaturdayDate.getDate() && d.month === selectedSaturdayDate.getMonth() && d.year === selectedSaturdayDate.getFullYear()).map(d => (
                                        <div key={d.id}>
                                            <div
                                                className={`list-item-interactive ${expandedReportId === d.id ? 'active' : ''}`}
                                                onClick={() => setExpandedReportId(expandedReportId === d.id ? null : d.id)}
                                            >
                                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: '600' }}>{d.displayName}</span>
                                                    <span className="badge badge-warning">Comp Required: {d.restCalc.deficit || '0.00'} hrs</span>
                                                    <span className="badge badge-danger">Total Due: {d.restCalc.totalDue || '45.00'} hrs</span>
                                                </div>
                                                <span onClick={(e) => { e.stopPropagation(); handleDeleteRestCalculation(d.id); }} style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 'bold' }}>✕</span>
                                            </div>
                                            {expandedReportId === d.id && (
                                                <div style={{ padding: '15px', background:'var(--light)', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                                        <div><strong>Start:</strong> {d.restCalc.startDay} {d.restCalc.startTime}</div>
                                                        <div><strong>End:</strong> {d.restCalc.endDay} {d.restCalc.endTime}</div>
                                                        <div><strong>Total Taken:</strong> {d.restCalc.hoursTaken} hrs</div>
                                                        {d.restCalc.compensatedBy && (
                                                            <div style={{gridColumn:'1 / -1', color:'var(--success)', fontWeight:'bold'}}>
                                                                Compensated by rest on {d.restCalc.compensatedBy.date} ({d.restCalc.compensatedBy.hours}h)
                                                            </div>
                                                        )}
                                                        {d.restCalc.notes && (
                                                            <div style={{gridColumn:'1 / -1', marginTop:'5px'}}>
                                                                <strong>Notes:</strong> {d.restCalc.notes}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ width: '250px', background:'rgba(94,106,210,0.12)', border: '1px solid #bae6fd', padding: '15px', height: 'fit-content', flexShrink: 0 }}>
                        <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#a5b0f0', fontSize: '16px' }}>Rota Cross-Check</h4>
                        
                        {/* Saturday Section */}
                        <div style={{ marginBottom: '20px' }}>
                            <p className="text-sm" style={{ marginBottom: '8px', color: '#0c4a6e', fontWeight: 'bold', borderBottom: '1px solid #bae6fd', paddingBottom: '4px' }}>
                                Saturday:
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {rotaDriversSat.length === 0 ? (
                                    <span className="text-sm" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No drivers.</span>
                                ) : (
                                    rotaDriversSat.map((driver) => {
                                        const isPresent = calcInputs[driver.initials];
                                        return (
                                            <div key={driver.initials} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                background:'var(--light)', padding: '6px 10px', borderRadius: '4px',
                                                borderLeft: `4px solid ${isPresent ? 'var(--success)' : 'var(--danger)'}`,
                                                fontSize: '12px', fontWeight: '500'
                                            }}>
                                                <span>{driver.displayName}</span>
                                                <span>{isPresent ? '✔' : '❌'}</span>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>

                        {/* Sunday Section */}
                        <div>
                            <p className="text-sm" style={{ marginBottom: '8px', color: '#0c4a6e', fontWeight: 'bold', borderBottom: '1px solid #bae6fd', paddingBottom: '4px' }}>
                                Sunday:
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {rotaDriversSun.length === 0 ? (
                                    <span className="text-sm" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No drivers.</span>
                                ) : (
                                    rotaDriversSun.map((driver) => {
                                        const isPresent = calcInputs[driver.initials];
                                        return (
                                            <div key={driver.initials} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                background:'var(--light)', padding: '6px 10px', borderRadius: '4px',
                                                borderLeft: `4px solid ${isPresent ? 'var(--success)' : 'var(--danger)'}`,
                                                fontSize: '12px', fontWeight: '500'
                                            }}>
                                                <span>{driver.displayName}</span>
                                                <span>{isPresent ? '✔' : '❌'}</span>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                </>
            )}
        </div>
    );
};

export default RestCalculator;